import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { ArrowLeft, Save, Trash2, Star, Plus, X, Check, FileDown } from "lucide-react";

const TABS = [
  { id: "details", label: "Details" },
  { id: "docs", label: "Documents & Bank" },
  { id: "reviews", label: "Performance" },
  { id: "resignation", label: "Resignation & Exit" },
];

const emptyReview = { period_label: "", review_date: new Date().toISOString().slice(0, 10), punctuality: 3, quality: 3, teamwork: 3, guest_interaction: 3, comments: "" };

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [saving, setSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState(emptyReview);
  const [addingReview, setAddingReview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hr/employees/${id}`);
      // Attach stable client-side keys to handover items so React can track reorders correctly.
      if (data?.resignation?.handover_items) {
        data.resignation.handover_items = data.resignation.handover_items.map((h) => ({ _key: crypto.randomUUID(), ...h }));
      }
      setEmp(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not load employee.");
    } finally { setLoading(false); }
  };

  const loadReviews = async () => {
    try {
      const { data } = await api.get(`/hr/employees/${id}/reviews`);
      setReviews(data);
    } catch (err) { console.error("Failed to load reviews:", err); }
  };

  useEffect(() => { load(); loadReviews(); }, [id]);

  const patch = (k, v) => setEmp((prev) => ({ ...prev, [k]: v }));
  const patchResignation = (k, v) => setEmp((prev) => ({ ...prev, resignation: { ...(prev.resignation || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...emp, ctc: emp.ctc === "" ? null : emp.ctc };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      // Strip client-only _key from handover items before sending to backend.
      if (payload.resignation?.handover_items) {
        payload.resignation = {
          ...payload.resignation,
          handover_items: payload.resignation.handover_items.map(({ _key, ...rest }) => rest),
        };
      }
      const { data } = await api.patch(`/hr/employees/${id}`, payload);
      if (data?.resignation?.handover_items) {
        data.resignation.handover_items = data.resignation.handover_items.map((h) => ({ _key: crypto.randomUUID(), ...h }));
      }
      setEmp(data);
      toast.success("Saved");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${emp.name}? Their performance reviews will also be removed.`)) return;
    try {
      await api.delete(`/hr/employees/${id}`);
      toast.success("Deleted");
      nav("/staff/employees");
    } catch { toast.error("Delete failed"); }
  };

  const addReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/employees/${id}/reviews`, {
        ...newReview,
        punctuality: Number(newReview.punctuality),
        quality: Number(newReview.quality),
        teamwork: Number(newReview.teamwork),
        guest_interaction: Number(newReview.guest_interaction),
      });
      toast.success("Review recorded");
      setNewReview(emptyReview);
      setAddingReview(false);
      loadReviews();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Review failed");
    }
  };

  const removeReview = async (rid) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/hr/employees/${id}/reviews/${rid}`);
      setReviews((prev) => prev.filter((r) => r.id !== rid));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/hr/employees/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Employee-${(emp?.name || "record").replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("PDF download failed:", err);
      toast.error("Could not download PDF");
    }
  };

  const downloadFullPdf = async () => {
    try {
      const res = await api.get(`/hr/employees/${id}/full.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Employee-Full-${(emp?.name || "record").replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Full dossier downloaded");
    } catch (err) {
      console.error("Full PDF failed:", err);
      toast.error("Could not download full dossier");
    }
  };

  const avgScore = useMemo(() => {
    if (!reviews.length) return null;
    const sum = reviews.reduce((s, r) => s + (r.overall || 0), 0);
    return (sum / reviews.length).toFixed(2);
  }, [reviews]);

  const addHandover = () => patchResignation("handover_items", [...((emp.resignation?.handover_items) || []), { _key: crypto.randomUUID(), text: "", done: false }]);
  const editHandover = (idx, key, val) => {
    const list = [...(emp.resignation?.handover_items || [])];
    list[idx] = { ...list[idx], [key]: val };
    patchResignation("handover_items", list);
  };
  const removeHandover = (idx) => {
    const list = [...(emp.resignation?.handover_items || [])];
    list.splice(idx, 1);
    patchResignation("handover_items", list);
  };

  if (loading) return <div data-testid="emp-loading">Loading...</div>;
  if (!emp) return <div>Not found.</div>;

  return (
    <div data-testid="employee-detail">
      <Link to="/staff/employees" data-testid="emp-back" className="text-sm inline-flex items-center gap-1 text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--forest))]">
        <ArrowLeft size={14} /> All employees
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {emp.photo_url ? (
            <img src={emp.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border border-[hsl(var(--rule))]" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-[hsl(var(--bg-2))] flex items-center justify-center text-2xl font-semibold text-[hsl(var(--ink-soft))]">
              {(emp.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="eyebrow">{emp.department || "—"}</div>
            <h1 className="mt-1 text-3xl">{emp.name}</h1>
            <div className="mt-1 text-sm text-[hsl(var(--ink-soft))]">
              {emp.role_title || "—"} · <span className="uppercase text-[10px] tracking-wider">{emp.status}</span>
              {avgScore && <> · Avg score {avgScore} ★</>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="emp-save" onClick={save} disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">
            <Save size={14} /> {saving ? "Saving..." : "Save changes"}
          </button>
          <button data-testid="emp-download-pdf" onClick={downloadPdf} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors">
            <FileDown size={12} /> Download PDF
          </button>
          <button data-testid="emp-download-full-pdf" onClick={downloadFullPdf} title="Full dossier: photo, KYC IDs, bank, notes" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors">
            <FileDown size={12} /> Full dossier
          </button>
          <button data-testid="emp-delete" onClick={remove} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 border-b border-[hsl(var(--rule))]">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm ${tab === t.id ? "border-b-2 border-[hsl(var(--forest))] font-semibold text-[hsl(var(--ink))]" : "text-[hsl(var(--ink-soft))]"}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {/* Details tab */}
      {tab === "details" && (
        <div data-testid="tab-details-panel" className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Full name"><input className="emp-inp" value={emp.name || ""} onChange={(e) => patch("name", e.target.value)} /></F>
          <F label="Status">
            <select className="emp-inp" value={emp.status || "active"} onChange={(e) => patch("status", e.target.value)}>
              <option value="active">Active</option>
              <option value="probation">Probation</option>
              <option value="on-notice">On notice</option>
              <option value="exited">Exited</option>
            </select>
          </F>
          <F label="Role / title"><input className="emp-inp" value={emp.role_title || ""} onChange={(e) => patch("role_title", e.target.value)} /></F>
          <F label="Department"><input className="emp-inp" value={emp.department || ""} onChange={(e) => patch("department", e.target.value)} /></F>
          <F label="Phone"><input className="emp-inp" value={emp.phone || ""} onChange={(e) => patch("phone", e.target.value)} /></F>
          <F label="Email"><input type="email" className="emp-inp" value={emp.email || ""} onChange={(e) => patch("email", e.target.value)} /></F>
          <F label="Employment type">
            <select className="emp-inp" value={emp.employment_type || "full-time"} onChange={(e) => patch("employment_type", e.target.value)}>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
            </select>
          </F>
          <F label="Joining date"><input type="date" className="emp-inp" value={emp.joining_date || ""} onChange={(e) => patch("joining_date", e.target.value)} /></F>
          <F label="CTC (₹ / year)"><input type="number" className="emp-inp" value={emp.ctc ?? ""} onChange={(e) => patch("ctc", e.target.value === "" ? null : Number(e.target.value))} /></F>
          <F label="Date of birth"><input type="date" className="emp-inp" value={emp.dob || ""} onChange={(e) => patch("dob", e.target.value)} /></F>
          <F label="Blood group"><input className="emp-inp" value={emp.blood_group || ""} onChange={(e) => patch("blood_group", e.target.value)} placeholder="O+" /></F>
          <F label="Address" full><textarea rows={2} className="emp-inp resize-none" value={emp.address || ""} onChange={(e) => patch("address", e.target.value)} /></F>
          <F label="Emergency contact name"><input className="emp-inp" value={emp.emergency_contact_name || ""} onChange={(e) => patch("emergency_contact_name", e.target.value)} /></F>
          <F label="Emergency contact phone"><input className="emp-inp" value={emp.emergency_contact_phone || ""} onChange={(e) => patch("emergency_contact_phone", e.target.value)} /></F>
          <F label="Aadhaar"><input className="emp-inp" value={emp.aadhaar || ""} onChange={(e) => patch("aadhaar", e.target.value)} /></F>
          <F label="PAN"><input className="emp-inp" value={emp.pan || ""} onChange={(e) => patch("pan", e.target.value)} /></F>
          <F label="Notes (internal)" full><textarea rows={2} className="emp-inp resize-none" value={emp.notes || ""} onChange={(e) => patch("notes", e.target.value)} /></F>
        </div>
      )}

      {/* Documents & Bank tab */}
      {tab === "docs" && (
        <div data-testid="tab-docs-panel" className="mt-6 space-y-8">
          <section>
            <div className="eyebrow">Documents</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <DocSlot label="Profile photo" url={emp.photo_url} onChange={(u) => patch("photo_url", u)} testid="doc-photo" />
              <DocSlot label="ID proof (Aadhaar / PAN)" url={emp.id_proof_url} onChange={(u) => patch("id_proof_url", u)} testid="doc-id" />
              <DocSlot label="Offer letter (PDF)" url={emp.offer_letter_url} onChange={(u) => patch("offer_letter_url", u)} testid="doc-offer" />
            </div>
          </section>
          <section>
            <div className="eyebrow">Bank details (salary)</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Account holder name"><input className="emp-inp" value={emp.bank_account_name || ""} onChange={(e) => patch("bank_account_name", e.target.value)} /></F>
              <F label="Bank name"><input className="emp-inp" value={emp.bank_name || ""} onChange={(e) => patch("bank_name", e.target.value)} /></F>
              <F label="Account number"><input className="emp-inp" value={emp.bank_account_number || ""} onChange={(e) => patch("bank_account_number", e.target.value)} /></F>
              <F label="IFSC"><input className="emp-inp" value={emp.bank_ifsc || ""} onChange={(e) => patch("bank_ifsc", e.target.value)} /></F>
            </div>
          </section>
        </div>
      )}

      {/* Performance tab */}
      {tab === "reviews" && (
        <div data-testid="tab-reviews-panel" className="mt-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-[hsl(var(--ink-soft))]">
              {reviews.length} review{reviews.length === 1 ? "" : "s"}{avgScore && <> · Avg overall <span className="text-[hsl(var(--ink))] font-semibold">{avgScore} ★</span></>}
            </div>
            <button data-testid="review-new-btn" onClick={() => setAddingReview((v) => !v)} className="btn-brass !py-2 !px-4 text-xs">
              <Plus size={12} /> {addingReview ? "Cancel" : "Log a review"}
            </button>
          </div>

          {addingReview && (
            <form data-testid="review-new-form" onSubmit={addReview} className="mt-4 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Period label"><input required placeholder="e.g., Q1 2026 or Feb 2026" data-testid="review-period" className="emp-inp" value={newReview.period_label} onChange={(e) => setNewReview({ ...newReview, period_label: e.target.value })} /></F>
              <F label="Review date"><input required type="date" data-testid="review-date" className="emp-inp" value={newReview.review_date} onChange={(e) => setNewReview({ ...newReview, review_date: e.target.value })} /></F>
              <RatingRow label="Punctuality" testid="review-punctuality" value={newReview.punctuality} onChange={(v) => setNewReview({ ...newReview, punctuality: v })} />
              <RatingRow label="Quality of work" testid="review-quality" value={newReview.quality} onChange={(v) => setNewReview({ ...newReview, quality: v })} />
              <RatingRow label="Teamwork" testid="review-teamwork" value={newReview.teamwork} onChange={(v) => setNewReview({ ...newReview, teamwork: v })} />
              <RatingRow label="Guest interaction" testid="review-guest" value={newReview.guest_interaction} onChange={(v) => setNewReview({ ...newReview, guest_interaction: v })} />
              <F label="Comments" full><textarea rows={3} className="emp-inp resize-none" value={newReview.comments} onChange={(e) => setNewReview({ ...newReview, comments: e.target.value })} /></F>
              <div className="md:col-span-2">
                <button type="submit" data-testid="review-submit" className="btn-brass">Save review</button>
              </div>
            </form>
          )}

          <div className="mt-6 space-y-3">
            {reviews.map((r) => (
              <article key={r.id} data-testid={`review-${r.id}`} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold">{r.period_label} <span className="text-[hsl(var(--ink-soft))] text-xs font-normal">· {r.review_date}</span></div>
                    <div className="text-xs text-[hsl(var(--ink-soft))] mt-0.5">by {r.reviewer_email}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-lg font-semibold text-[hsl(var(--forest))]">{r.overall.toFixed(2)} ★</div>
                    <button data-testid={`review-delete-${r.id}`} onClick={() => removeReview(r.id)} className="text-[10px] uppercase tracking-wider text-[hsl(6_62%_45%)] hover:underline">Delete</button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <ScoreCell label="Punctuality" value={r.punctuality} />
                  <ScoreCell label="Quality" value={r.quality} />
                  <ScoreCell label="Teamwork" value={r.teamwork} />
                  <ScoreCell label="Guest interaction" value={r.guest_interaction} />
                </div>
                {r.comments && <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] italic">"{r.comments}"</p>}
              </article>
            ))}
            {reviews.length === 0 && (
              <div className="p-8 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center text-sm text-[hsl(var(--ink-soft))]">
                No reviews yet. Log the first one above.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resignation tab */}
      {tab === "resignation" && (
        <div data-testid="tab-resignation-panel" className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Notice date"><input type="date" className="emp-inp" value={emp.resignation?.notice_date || ""} onChange={(e) => patchResignation("notice_date", e.target.value)} /></F>
          <F label="Last working day"><input type="date" className="emp-inp" value={emp.resignation?.last_working_day || ""} onChange={(e) => patchResignation("last_working_day", e.target.value)} /></F>
          <F label="Reason" full><textarea rows={2} className="emp-inp resize-none" value={emp.resignation?.reason || ""} onChange={(e) => patchResignation("reason", e.target.value)} /></F>
          <F label="F&F status">
            <select className="emp-inp" value={emp.resignation?.fnf_status || "pending"} onChange={(e) => patchResignation("fnf_status", e.target.value)}>
              <option value="pending">Pending</option>
              <option value="cleared">Cleared</option>
            </select>
          </F>
          <F label="F&F notes"><input className="emp-inp" value={emp.resignation?.fnf_notes || ""} onChange={(e) => patchResignation("fnf_notes", e.target.value)} /></F>
          <div className="md:col-span-2">
            <div className="eyebrow">Handover checklist</div>
            <ul className="mt-3 space-y-2">
              {(emp.resignation?.handover_items || []).map((h, i) => (
                <li key={h._key || `h-${i}`} className="flex items-center gap-2" data-testid={`handover-${i}`}>
                  <button type="button" onClick={() => editHandover(i, "done", !h.done)} className={`w-5 h-5 border ${h.done ? "bg-[hsl(var(--forest))] border-[hsl(var(--forest))] text-white" : "border-[hsl(var(--rule))]"} rounded flex items-center justify-center`}>
                    {h.done && <Check size={12} />}
                  </button>
                  <input className={`emp-inp flex-1 ${h.done ? "line-through text-[hsl(var(--ink-soft))]" : ""}`} value={h.text} onChange={(e) => editHandover(i, "text", e.target.value)} placeholder="e.g., Return uniform, hand over locker key..." />
                  <button type="button" onClick={() => removeHandover(i)} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(6_62%_45%)]"><X size={14} /></button>
                </li>
              ))}
            </ul>
            <button type="button" data-testid="handover-add" onClick={addHandover} className="mt-3 text-xs text-[hsl(var(--forest))] font-semibold">+ Add checklist item</button>
          </div>
        </div>
      )}

      <style>{`
        .emp-inp {
          width: 100%;
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.65rem 0.85rem;
          font-family: inherit;
          font-size: 0.9rem;
          color: hsl(var(--ink));
        }
        .emp-inp:focus { outline: none; border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))] mb-2">{label}</span>
      {children}
    </label>
  );
}

function RatingRow({ label, value, onChange, testid }) {
  return (
    <div>
      <span className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))] mb-2">{label}</span>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} className={`p-1.5 rounded ${n <= value ? "text-[hsl(var(--brass))]" : "text-[hsl(var(--rule))]"}`}>
            <Star size={20} fill={n <= value ? "currentColor" : "none"} />
          </button>
        ))}
        <span className="ml-2 self-center text-sm text-[hsl(var(--ink-soft))]">{value}/5</span>
      </div>
    </div>
  );
}

function ScoreCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">{label}</div>
      <div className="mt-0.5 flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} size={12} className="text-[hsl(var(--brass))]" fill={n <= value ? "currentColor" : "none"} />
        ))}
        <span className="ml-1 text-[hsl(var(--ink-soft))]">{value}</span>
      </div>
    </div>
  );
}

function DocSlot({ label, url, onChange, testid }) {
  const isPdf = (u) => typeof u === "string" && u.toLowerCase().endsWith(".pdf");
  return (
    <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4">
      <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">{label}</div>
      {url ? (
        <div className="mt-3">
          {isPdf(url) ? (
            <a href={url} target="_blank" rel="noreferrer" className="block text-sm text-[hsl(var(--forest))] font-semibold underline underline-offset-4 truncate">
              📄 Open PDF
            </a>
          ) : (
            <img src={url} alt="" className="w-full h-32 object-cover" />
          )}
        </div>
      ) : (
        <div className="mt-3 h-32 bg-[hsl(var(--bg))] border border-dashed border-[hsl(var(--rule))] flex items-center justify-center text-[hsl(var(--ink-soft))] text-xs">
          Not uploaded
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <ImageUploader testid={testid} label={url ? "Replace" : "Upload"} onUploaded={onChange} />
        {url && (
          <button type="button" onClick={() => onChange("")} className="text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
